import React, { useState, useEffect, useRef, useCallback } from 'react';
import { C, St, FONT_BODY, CARD_STYLE } from '../theme';
import { supabase } from '../supabase';
import { SearchBar } from '../components/UI';
import { SpiderChart } from '../components/Charts';
import { calcAvg1RM, calcVolume, calcSpiderMetrics } from '../science';
import { _globalSettings } from '../constants';

// ─── Unified feed builder ──────────────────────────────────────────────────────
// Groups all events by (date, friendId) → one card per friend per day
function buildUnifiedFeed(friendsSessionsMap, friendsSchedeMap, friendsWeightMap, friends) {
  // Collect raw events per friend per date
  const buckets = {}; // key: `${date}__${friendId}`

  const getBucket = (date, fid, fName, fAvatar, fSesso) => {
    const key = `${date}__${fid}`;
    if (!buckets[key]) {
      buckets[key] = { date, friendId: fid, friendName: fName, friendAvatar: fAvatar, friendSesso: fSesso,
        sessions: [], loads: [], schede: [], weights: [] };
    }
    return buckets[key];
  };

  friends.forEach(friend => {
    const fid = friend.user_id;
    const fName = friend.display_name || friend.username || 'Utente';
    const fAvatar = friend.avatar_index || 1;
    const fSesso = friend.sesso || 'M';

    // Sessions + load/load_down
    const sessions = [...(friendsSessionsMap[fid] || [])].sort(
      (a, b) => (a.date || '').localeCompare(b.date || '')
    );
    const exHistory = {};
    sessions.forEach(sess => {
      const date = sess.date;
      if (!date) return;
      const wn = sess.workout_name || sess.workoutName;
      if (wn) getBucket(date, fid, fName, fAvatar, fSesso).sessions.push(wn);

      (sess.exercises || []).forEach(ex => {
        const name = ex.name;
        if (!name) return;
        const maxW = Math.max(0, ...(ex.sets || []).map(s => parseFloat(s.weight) || 0));
        const prev = exHistory[name];
        if (prev && maxW > 0) {
          const delta = +(maxW - prev.maxW).toFixed(1);
          if (Math.abs(delta) >= 2.5) {
            getBucket(date, fid, fName, fAvatar, fSesso).loads.push({ exName: name, delta, newW: maxW });
          }
        }
        exHistory[name] = { maxW };
      });
    });

    // Schede
    (friendsSchedeMap[fid] || []).forEach(s => {
      const date = s.created_at ? s.created_at.slice(0, 10) : null;
      if (s.name && date) getBucket(date, fid, fName, fAvatar, fSesso).schede.push(s.name);
    });

    // Weight
    (friendsWeightMap[fid] || []).forEach(w => {
      if (w.weight && w.date) getBucket(w.date, fid, fName, fAvatar, fSesso).weights.push(w.weight);
    });
  });

  // Flatten buckets → sorted flat list, then group by date
  const allBuckets = Object.values(buckets)
    .filter(b => b.sessions.length || b.loads.length || b.schede.length || b.weights.length)
    .sort((a, b) => b.date.localeCompare(a.date));

  const byDate = [];
  allBuckets.forEach(bucket => {
    const last = byDate[byDate.length - 1];
    if (last && last.date === bucket.date) {
      last.buckets.push(bucket);
    } else {
      byDate.push({ date: bucket.date, buckets: [bucket] });
    }
  });

  return byDate;
}

function formatDateHeader(dateStr) {
  if (!dateStr) return '';
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  if (dateStr === today) return 'Oggi';
  if (dateStr === yesterday) return 'Ieri';
  return new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
}

const memoUrl = n => `https://cdn.jsdelivr.net/gh/alohe/memojis@main/png/memo_${n}.png`;

// ─── Friend day card — avatar centered, discorsive text ──────────────────────
function FriendDayCard({ bucket, onAvatarClick }) {
  const { friendName: name, friendAvatar, friendId, friendSesso, sessions, loads, schede, weights } = bucket;
  const allenato = friendSesso === 'F' ? 'allenata' : 'allenato';

  // Build events: { color, bg, label, text }
  const events = [];

  if (sessions.length) {
    events.push({ color: C.blue, bg: '#007AFF14', label: 'Sessione',
      text: `Si è ${allenato} su ${sessions.join(', ')}.` });
  }
  loads.forEach(l => l.delta > 0
    ? events.push({ color: C.green, bg: '#34C75914', label: 'Carico',
        text: `Ha aumentato il carico su ${l.exName} di ${l.delta} kg, arrivando a ${l.newW} kg.` })
    : events.push({ color: C.red, bg: '#FF3B3014', label: 'Ridotto',
        text: `Ha ridotto il carico su ${l.exName} di ${Math.abs(l.delta)} kg, ora a ${l.newW} kg.` })
  );
  schede.forEach(s => events.push({ color: C.purple, bg: '#AF52DE14', label: 'Scheda',
    text: `Ha creato una nuova scheda: ${s}.` }));
  weights.forEach(w => events.push({ color: C.cyan, bg: '#32ADE614', label: 'Peso',
    text: `Ha registrato il peso: ${w} kg.` }));

  return (
    <div style={{ padding: '16px 16px 14px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      <button onClick={() => onAvatarClick(friendId)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
        <img src={memoUrl(friendAvatar)} alt=""
          style={{ width: 96, height: 96, borderRadius: 48, background: C.fill, display: 'block' }}/>
      </button>

      <div style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: FONT_BODY, textAlign: 'center' }}>
        {name}
      </div>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {events.map((ev, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: 8, alignItems: 'baseline' }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: ev.color, background: ev.bg,
              borderRadius: 20, padding: '3px 9px', textTransform: 'uppercase', letterSpacing: 0.4,
              fontFamily: FONT_BODY, textAlign: 'center' }}>
              {ev.label}
            </span>
            <span style={{ fontSize: 13, color: C.muted, fontFamily: FONT_BODY, lineHeight: 1.5 }}>
              {ev.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Unified feed section ──────────────────────────────────────────────────────
function UnifiedFeedSection({ groups, onAvatarClick }) {
  const [expanded, setExpanded] = useState(false);
  if (!groups.length) return null;

  const INITIAL_GROUPS = 3;
  const visible = expanded ? groups : groups.slice(0, INITIAL_GROUPS);
  const hiddenCount = groups.slice(INITIAL_GROUPS).reduce((s, g) => s + g.buckets.length, 0);

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, textTransform: 'uppercase',
        letterSpacing: 0.4, paddingLeft: 4, marginBottom: 10, fontFamily: FONT_BODY }}>
        Attività
      </div>
      {visible.map(group => (
        <div key={group.date}>
          {/* Date label outside card */}
          <div style={{ fontSize: 12, fontWeight: 600, color: C.subtle, fontFamily: FONT_BODY,
            paddingLeft: 4, marginBottom: 8, letterSpacing: 0.2 }}>
            {formatDateHeader(group.date)}
          </div>
          {/* One card per friend per day, events inside */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
            {group.buckets.map((bucket, i) => (
              <div key={`${bucket.friendId}-${i}`} style={{ ...CARD_STYLE, overflow: 'hidden' }}>
                <FriendDayCard bucket={bucket} onAvatarClick={onAvatarClick}/>
              </div>
            ))}
          </div>
        </div>
      ))}
      {!expanded && groups.length > INITIAL_GROUPS && (
        <button onClick={() => setExpanded(true)}
          style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer',
            color: C.blue, fontSize: 13, fontWeight: 600, fontFamily: FONT_BODY,
            padding: '8px 0', textAlign: 'center' }}>
          Mostra altri {hiddenCount} eventi
        </button>
      )}
    </div>
  );
}

// ─── Friends list (vertical list — avatar + name + subtext per row) ──────────
function FriendsList({ friends, friendsSessionsMap, pendingIn, pendingOut, onSelect, onRespond }) {
  const hasAny = friends.length > 0 || pendingIn.length > 0 || pendingOut.length > 0;
  if (!hasAny) return null;

  const rowStyle = { display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0' };

  const allItems = [
    ...friends.map(f => ({ type: 'friend', data: f })),
    ...pendingIn.map(r => ({ type: 'pendingIn', data: r })),
    ...pendingOut.map(r => ({ type: 'pendingOut', data: r })),
  ];

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, textTransform: 'uppercase',
        letterSpacing: 0.4, paddingLeft: 4, marginBottom: 8, fontFamily: FONT_BODY }}>
        I tuoi amici
      </div>
      <div>
        {allItems.map((item, idx) => {
          const isLast = idx === allItems.length - 1;
          const sep = !isLast && (
            <div style={{ height: '0.5px', background: 'rgba(60,60,67,0.12)', marginLeft: 66 }}/>
          );

          if (item.type === 'friend') {
            const f = item.data;
            const sessions = friendsSessionsMap[f.user_id] || [];
            const lastDate = sessions[0]?.date;
            const subtext = lastDate ? formatDateHeader(lastDate) : 'Nessuna attività';
            return (
              <div key={f.user_id}>
                <button onClick={() => onSelect(f)}
                  style={{ ...rowStyle, width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  <img src={memoUrl(f.avatar_index || 1)} alt=""
                    style={{ width: 44, height: 44, borderRadius: 22, background: C.fill, flexShrink: 0 }}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: FONT_BODY,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.display_name || f.username || 'Utente'}
                    </div>
                    <div style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY, marginTop: 1 }}>{subtext}</div>
                  </div>
                  <span style={{ color: C.subtle, fontSize: 16, paddingRight: 2 }}>›</span>
                </button>
                {sep}
              </div>
            );
          }

          if (item.type === 'pendingIn') {
            const req = item.data;
            const u = req.requester;
            const name = u?.display_name || u?.username || 'Utente';
            return (
              <div key={req.id}>
                <div style={rowStyle}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <img src={memoUrl(u?.avatar_index || 1)} alt=""
                      style={{ width: 44, height: 44, borderRadius: 22, background: C.fill, display: 'block' }}/>
                    <div style={{ position: 'absolute', top: 0, right: 0, width: 12, height: 12, borderRadius: 6,
                      background: C.red, border: `2px solid ${C.bg}` }}/>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: FONT_BODY,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                    <div style={{ fontSize: 12, color: C.blue, fontFamily: FONT_BODY, marginTop: 1, fontWeight: 600 }}>ti ha aggiunto</div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button onClick={() => onRespond(req.id, 'accepted', name)}
                      style={{ background: C.green, border: 'none', borderRadius: 8, padding: '6px 14px',
                        fontSize: 12, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: FONT_BODY }}>
                      Accetta
                    </button>
                    <button onClick={() => onRespond(req.id, 'rejected', null)}
                      style={{ background: 'rgba(120,120,128,0.12)', border: 'none', borderRadius: 8, padding: '6px 10px',
                        fontSize: 12, fontWeight: 600, color: C.subtle, cursor: 'pointer', fontFamily: FONT_BODY }}>
                      No
                    </button>
                  </div>
                </div>
                {sep}
              </div>
            );
          }

          // pendingOut
          const req = item.data;
          const u = req.addressee;
          const name = u?.display_name || u?.username || 'Utente';
          return (
            <div key={req.id}>
              <div style={{ ...rowStyle, opacity: 0.5 }}>
                <img src={memoUrl(u?.avatar_index || 1)} alt=""
                  style={{ width: 44, height: 44, borderRadius: 22, background: C.fill, flexShrink: 0 }}/>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: FONT_BODY,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                  <div style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY, marginTop: 1 }}>In attesa</div>
                </div>
              </div>
              {sep}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Friend profile sheet ──────────────────────────────────────────────────────
function FriendProfileSheet({ friend, friendSessions, myDb, onClose }) {
  const profilo = _globalSettings.profilo || {};
  const myPeso = parseFloat(profilo.peso) || null;

  const sessions = friendSessions || [];

  // Stats
  const totalSessions = sessions.length;
  const totalVol = Math.round(sessions.reduce((s, sess) =>
    s + (sess.exercises || []).reduce((t, e) => t + calcVolume(e.sets || []), 0), 0));
  const best1RM = sessions.reduce((best, sess) => {
    (sess.exercises || []).forEach(e => { const rm = calcAvg1RM(e.sets || []); if (rm > best) best = rm; });
    return best;
  }, 0);

  const formatVol = v => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v);

  // Spider data
  const mySorted = [...(myDb?.sessions || [])].sort((a, b) => b.date.localeCompare(a.date));
  const friendSorted = [...sessions].sort((a, b) => b.date.localeCompare(a.date));
  const myLast = mySorted[0];
  const friendLast = friendSorted[0];
  const mySpider = myLast ? calcSpiderMetrics(myLast, myDb.sessions, myPeso) : null;
  const friendSpider = friendLast ? calcSpiderMetrics(friendLast, sessions, null) : null;

  // Last 5 events (session + load only)
  const exHistory = {};
  const recentEvents = [];
  const chronSessions = [...sessions].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  chronSessions.forEach(sess => {
    const wn = sess.workout_name || sess.workoutName;
    if (wn) recentEvents.push({ type: 'session', date: sess.date, workoutName: wn });
    (sess.exercises || []).forEach(ex => {
      const name = ex.name;
      if (!name) return;
      const maxW = Math.max(0, ...(ex.sets || []).map(s => parseFloat(s.weight) || 0));
      const prev = exHistory[name];
      if (prev && maxW > 0) {
        const delta = +(maxW - prev.maxW).toFixed(1);
        if (Math.abs(delta) >= 2.5) {
          recentEvents.push({ type: delta > 0 ? 'load' : 'load_down', date: sess.date, exName: name, delta, newW: maxW });
        }
      }
      exHistory[name] = { maxW };
    });
  });
  recentEvents.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const last5 = recentEvents.slice(0, 5);

  const friendName = friend.display_name || friend.username || 'Utente';

  // Profilo atletico dell'amico (da Supabase)
  const fPeso    = parseFloat(friend.peso) || null;
  const fAltezza = parseInt(friend.altezza) || null;
  const fEta     = parseInt(friend.eta) || null;
  const fSesso   = friend.sesso || 'M';
  const fLivello = parseFloat(friend.livello_attivita) || 1.55;

  const friendBmiData = fPeso && fAltezza ? (() => {
    const bmi    = Math.round((fPeso / Math.pow(fAltezza / 100, 2)) * 10) / 10;
    const bmiCat = bmi < 18.5 ? 'Sottopeso' : bmi < 25 ? 'Normopeso' : bmi < 30 ? 'Sovrappeso' : 'Obesità';
    const bmiCol = bmi < 18.5 ? C.amber : bmi < 25 ? C.green : bmi < 30 ? C.amber : C.red;
    const bmr    = fEta ? Math.round(fSesso === 'M' ? 10*fPeso + 6.25*fAltezza - 5*fEta + 5 : 10*fPeso + 6.25*fAltezza - 5*fEta - 161) : null;
    const tdee   = bmr ? Math.round(bmr * fLivello) : null;
    return { bmi, bmiCat, bmiCol, bmr, tdee };
  })() : null;

  const profiloRows = [
    fPeso    && { label: 'Peso',        value: `${fPeso} kg` },
    friend.peso_target && { label: 'Peso target', value: `${parseFloat(friend.peso_target)} kg` },
    fAltezza && { label: 'Altezza',     value: `${fAltezza} cm` },
    fEta     && { label: 'Età',         value: `${fEta} anni` },
    friend.sesso && { label: 'Sesso',   value: friend.sesso === 'M' ? 'Maschio' : 'Femmina' },
    friend.obiettivo && { label: 'Obiettivo', value: friend.obiettivo === 'cut' ? 'Cut' : friend.obiettivo === 'bulk' ? 'Bulk' : 'Recomp' },
    friend.esperienza && { label: 'Livello', value: friend.esperienza },
  ].filter(Boolean);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: C.bg, overflowY: 'auto',
      paddingBottom: 'max(40px, calc(env(safe-area-inset-bottom, 0px) + 40px))' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'max(56px, calc(env(safe-area-inset-top, 0px) + 16px)) 20px 16px',
        background: C.bg, position: 'sticky', top: 0, zIndex: 1 }}>
        <div style={{ width: 36 }}/>
        <div style={{ fontSize: 17, fontWeight: 600, color: C.text, fontFamily: FONT_BODY }}>{friendName}</div>
        <button onClick={onClose}
          style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(120,120,128,0.14)',
            border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <div style={{ padding: '0 16px' }}>

        {/* Avatar + name */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <img src={memoUrl(friend.avatar_index || 1)} alt=""
            style={{ width: 80, height: 80, borderRadius: 40, background: C.fill,
              boxShadow: '0 4px 20px rgba(0,0,0,0.12), 0 0 0 3px rgba(255,255,255,0.9)' }}/>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: C.text, fontFamily: FONT_BODY, letterSpacing: -0.4 }}>{friendName}</div>
            {friend.username && (
              <div style={{ fontSize: 14, color: C.subtle, fontFamily: FONT_BODY, marginTop: 2 }}>@{friend.username}</div>
            )}
          </div>
        </div>

        {/* Profilo atleta */}
        {profiloRows.length > 0 && (
          <div style={{ ...CARD_STYLE, padding: '16px 20px', marginBottom: 12 }}>
            <div style={St.secTit}>PROFILO ATLETA</div>
            <div style={{ marginTop: 8 }}>
              {profiloRows.map((r, i, arr) => (
                <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: i < arr.length - 1 ? `0.5px solid ${C.border}` : 'none' }}>
                  <span style={{ fontSize: 15, color: C.subtle, fontFamily: FONT_BODY }}>{r.label}</span>
                  <span style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: FONT_BODY }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metriche corporee */}
        {friendBmiData && (
          <div style={{ ...CARD_STYLE, padding: '16px 20px', marginBottom: 12 }}>
            <div style={St.secTit}>METRICHE CORPOREE</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <div style={{ flex: 1, textAlign: 'center', padding: '14px 8px', background: `${friendBmiData.bmiCol}12`, borderRadius: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6, fontFamily: FONT_BODY }}>BMI</div>
                <div style={{ fontSize: 28, fontWeight: 800, color: friendBmiData.bmiCol, letterSpacing: -1, lineHeight: 1, fontFamily: FONT_BODY }}>{friendBmiData.bmi.toFixed(1)}</div>
                <div style={{ fontSize: 10, fontWeight: 600, color: friendBmiData.bmiCol, marginTop: 5, fontFamily: FONT_BODY }}>{friendBmiData.bmiCat}</div>
              </div>
              {friendBmiData.bmr && (
                <div style={{ flex: 1, textAlign: 'center', padding: '14px 8px', background: `${C.blue}10`, borderRadius: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6, fontFamily: FONT_BODY }}>BMR</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: -0.5, lineHeight: 1, fontFamily: FONT_BODY }}>{friendBmiData.bmr.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: C.subtle, marginTop: 5, fontFamily: FONT_BODY }}>kcal/die</div>
                </div>
              )}
              {friendBmiData.tdee && (
                <div style={{ flex: 1, textAlign: 'center', padding: '14px 8px', background: `${C.green}10`, borderRadius: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.subtle, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 6, fontFamily: FONT_BODY }}>TDEE</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: C.text, letterSpacing: -0.5, lineHeight: 1, fontFamily: FONT_BODY }}>{friendBmiData.tdee.toLocaleString()}</div>
                  <div style={{ fontSize: 10, color: C.subtle, marginTop: 5, fontFamily: FONT_BODY }}>kcal/die</div>
                </div>
              )}
            </div>
            {!friendBmiData.bmr && (
              <div style={{ fontSize: 12, color: C.muted, textAlign: 'center', marginTop: 10, fontFamily: FONT_BODY }}>
                Eta non condivisa, BMR non calcolabile
              </div>
            )}
          </div>
        )}

        {/* Statistiche */}
        {totalSessions > 0 && (() => {
          const cutoff30 = new Date(); cutoff30.setDate(cutoff30.getDate() - 30);
          const last30 = sessions.filter(s => s.date && new Date(s.date) >= cutoff30);
          const freqWeek = last30.length > 0 ? `${(last30.length / 4.3).toFixed(1)}x / sett` : null;
          const avgVol = last30.length > 0
            ? `${Math.round(last30.reduce((s, sess) =>
                s + (sess.exercises || []).reduce((t, e) => t + calcVolume(e.sets || []), 0), 0) / last30.length).toLocaleString()} kg`
            : null;
          const exSet = new Set();
          sessions.forEach(s => (s.exercises || []).forEach(e => e.name && exSet.add(e.name)));
          const statRows = [
            { label: 'Sessioni totali',  value: totalSessions,                                sub: null },
            { label: 'Volume totale',    value: totalVol > 0 ? `${formatVol(totalVol)} kg` : '—', sub: null },
            { label: 'Best 1RM',         value: best1RM > 0 ? `${best1RM.toFixed(1)} kg` : '—', sub: null },
            freqWeek && { label: 'Frequenza',      value: freqWeek, sub: 'ultimi 30gg' },
            avgVol   && { label: 'Volume medio',   value: avgVol,   sub: 'per sessione' },
            exSet.size > 0 && { label: 'Esercizi',  value: exSet.size, sub: 'nel repertorio' },
          ].filter(Boolean);
          return (
            <div style={{ ...CARD_STYLE, padding: '16px 20px', marginBottom: 12 }}>
              <div style={St.secTit}>STATISTICHE</div>
              <div style={{ marginTop: 8 }}>
                {statRows.map((r, i, arr) => (
                  <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 0', borderBottom: i < arr.length - 1 ? `0.5px solid ${C.border}` : 'none' }}>
                    <span style={{ fontSize: 15, color: C.subtle, fontFamily: FONT_BODY }}>
                      {r.label}{r.sub ? <span style={{ fontSize: 12, color: C.subtle, marginLeft: 4 }}>({r.sub})</span> : null}
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: FONT_BODY }}>{r.value}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Spider chart */}
        {mySpider && friendSpider && (
          <div style={{ ...CARD_STYLE, padding: '16px 16px 12px', marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: FONT_BODY, marginBottom: 4, letterSpacing: -0.2 }}>
              Confronto
            </div>
            <SpiderChart data={mySpider} compareData={friendSpider}/>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 5, background: C.blue }}/>
                <span style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY }}>Tu</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: 5, background: C.purple }}/>
                <span style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY }}>{friendName}</span>
              </div>
            </div>
          </div>
        )}

        {/* Benchmark legend */}
        {(mySpider || friendSpider) && (() => {
          const SPIDER_DESCS = [
            { key: 'FR', label: 'Forza Relativa',            color: C.blue,   desc: 'Miglior 1RM stimato (Brzycki) diviso il peso corporeo. 100 corrisponde a sollevare 2.5 volte il proprio peso.' },
            { key: 'SS', label: 'Stabilità della Progressione', color: C.green,  desc: 'Quanto è costante il volume nelle ultime 4 sessioni sull\'esercizio principale. Alta = progressione lineare e stabile.' },
            { key: 'DL', label: 'Densità Lavorativa',          color: C.amber,  desc: 'Volume dell\'ultima sessione rispetto alla migliore sessione personale. Misura quanto si è vicini al proprio picco.' },
            { key: 'CCS', label: 'Capacità di Carico Sostenuto', color: C.red,   desc: 'Rapporto tra il volume della seconda metà e della prima metà della sessione. Indica resistenza alla fatica muscolare.' },
            { key: 'EV', label: 'Efficienza Volume',           color: C.purple, desc: 'Reps stimolanti per serie. 5 reps stimolanti per serie = 100. Misura la qualita del lavoro, non la quantita.' },
          ];
          const chartData = friendSpider || mySpider;
          return (
            <div style={{ ...CARD_STYLE, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.subtle, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: FONT_BODY, marginBottom: 12 }}>
                Legenda benchmark
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {SPIDER_DESCS.map(item => {
                  const val = chartData?.find(d => d.metric === item.key)?.value;
                  return (
                    <div key={item.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: item.color,
                        background: `${item.color}18`, borderRadius: 6, padding: '2px 7px',
                        fontFamily: FONT_BODY, flexShrink: 0, marginTop: 1 }}>
                        {item.key}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.text, fontFamily: FONT_BODY }}>{item.label}</span>
                          {val != null && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: item.color, fontFamily: FONT_BODY }}>{val}</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY, lineHeight: 1.5 }}>{item.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Recent events */}
        {last5.length > 0 && (
          <div style={{ ...CARD_STYLE, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ padding: '14px 16px 6px' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: FONT_BODY, letterSpacing: -0.2 }}>
                Ultimi eventi
              </div>
            </div>
            <div style={{ padding: '0 16px 6px' }}>
              {last5.map((ev, i) => {
                const color = ev.type === 'load' ? C.green : ev.type === 'load_down' ? C.red : C.blue;
                const bg = ev.type === 'load' ? '#34C75914' : ev.type === 'load_down' ? '#FF3B3014' : '#007AFF14';
                const label = ev.type === 'load' ? 'Carico' : ev.type === 'load_down' ? 'Calo' : 'Sessione';
                const text = ev.type === 'session'
                  ? ev.workoutName
                  : ev.type === 'load'
                  ? `+${ev.delta}kg su ${ev.exName}`
                  : `${ev.delta}kg su ${ev.exName}`;
                const dateStr = ev.date
                  ? new Date(ev.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })
                  : '';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10,
                    paddingTop: 10, paddingBottom: 10,
                    borderBottom: i < last5.length - 1 ? '0.5px solid rgba(60,60,67,0.12)' : 'none' }}>
                    <div style={{ width: 8, height: 8, borderRadius: 4, background: color, marginTop: 5, flexShrink: 0 }}/>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color, background: bg,
                          borderRadius: 5, padding: '1px 6px', textTransform: 'uppercase',
                          letterSpacing: 0.4, fontFamily: FONT_BODY }}>{label}</span>
                        {dateStr && <span style={{ fontSize: 11, color: C.subtle, fontFamily: FONT_BODY }}>{dateStr}</span>}
                      </div>
                      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.4, fontFamily: FONT_BODY }}>{text}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {totalSessions === 0 && (
          <div style={{ textAlign: 'center', padding: '32px 20px', background: 'rgba(120,120,128,0.05)', borderRadius: 20 }}>
            <div style={{ fontSize: 13, color: C.subtle, fontFamily: FONT_BODY }}>
              Nessuna sessione registrata ancora.
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── Orbit avatars (empty state) ───────────────────────────────────────────────
const ORBIT_CONFIG = [
  { radius: 95, size: 42, speed: 20, startAngle: 56,  memos: [1,  8]  },
  { radius: 94, size: 68, speed: 20, startAngle: 90,  memos: [3,  15] },
  { radius:104, size: 58, speed: 20, startAngle: 157, memos: [7,  22] },
  { radius: 96, size: 39, speed: 20, startAngle: 220, memos: [12, 28] },
  { radius:109, size: 32, speed: 20, startAngle: 236, memos: [5,  18] },
  { radius: 89, size: 70, speed: 20, startAngle: 270, memos: [25, 30] },
  { radius: 99, size: 58, speed: 20, startAngle: 337, memos: [9,  2]  },
];

function OrbitAvatars() {
  const [ticks, setTicks] = useState(() => ORBIT_CONFIG.map(() => 0));
  useEffect(() => {
    const t = setInterval(() => setTicks(prev => prev.map((v, i) => (v + 1) % ORBIT_CONFIG[i].memos.length)), 3000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ position: 'relative', width: 260, height: 260, flexShrink: 0 }}>
      <style>{`
        @keyframes gp-orbit-cw  { from { transform: rotate(0deg); } to { transform: rotate(360deg);  } }
        @keyframes gp-orbit-ccw { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
      `}</style>
      <div style={{ position:'absolute', left:'50%', top:'50%', width:68, height:68, marginLeft:-34, marginTop:-34, borderRadius:34,
        background:'linear-gradient(160deg,#ffffff 0%,rgba(120,120,128,0.04) 100%)',
        boxShadow:'0 4px 20px rgba(0,0,0,0.10),0 0 0 0.5px rgba(0,0,0,0.06)',
        display:'flex', alignItems:'center', justifyContent:'center' }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={C.subtle} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="7" r="3"/><path d="M2 20a7 7 0 0114 0"/>
          <circle cx="17" cy="7" r="2.5"/><path d="M15.5 20a5 5 0 015-4.5"/>
        </svg>
      </div>
      {ORBIT_CONFIG.map((cfg, i) => {
        const delay = `${-((cfg.startAngle / 360) * cfg.speed).toFixed(3)}s`;
        return (
          <div key={i} style={{ position:'absolute', left:'50%', top:'50%', width:0, height:0, transformOrigin:'0 0',
            animation:`gp-orbit-cw ${cfg.speed}s linear infinite`, animationDelay:delay }}>
            <div style={{ position:'absolute', left:cfg.radius-cfg.size/2, top:-cfg.size/2, width:cfg.size, height:cfg.size,
              animation:`gp-orbit-ccw ${cfg.speed}s linear infinite`, animationDelay:delay }}>
              <img src={memoUrl(cfg.memos[ticks[i]])} alt=""
                style={{ width:cfg.size, height:cfg.size, borderRadius:cfg.size/2,
                  border:'2.5px solid rgba(255,255,255,0.95)', background:C.fill,
                  boxShadow:'0 2px 10px rgba(0,0,0,0.12)', display:'block', transition:'opacity 0.4s' }}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Supabase helpers ──────────────────────────────────────────────────────────
async function dbSearchUsers(query, uid) {
  if (!query.trim()) return [];
  try {
    const clean = query.trim().replace(/^@/, '');
    const col = query.includes('.') && query.includes('@') ? 'email' : 'username';
    const { data, error } = await supabase.from('profiles')
      .select('user_id,username,display_name,avatar_index,eta,peso,altezza,sesso,peso_target,livello_attivita,esperienza,obiettivo')
      .neq('user_id', uid).ilike(col, `%${clean}%`).limit(8);
    if (error) console.error('[Amici] search error:', error);
    return data || [];
  } catch(e) { console.error('[Amici] search exception:', e); return []; }
}

async function dbSendRequest(rid, aid) {
  try {
    const { error } = await supabase.from('friendships').insert({ requester_id: rid, addressee_id: aid });
    if (error) console.error('[Amici] send request error:', error);
    return !error;
  } catch(e) { console.error('[Amici] send request exception:', e); return false; }
}

async function dbRespond(fid, status) {
  try {
    const { error } = await supabase.from('friendships').update({ status }).eq('id', fid);
    if (error) console.error('[Amici] respond error:', error);
  } catch(e) { console.error('[Amici] respond exception:', e); }
}

async function dbGetFriends(uid) {
  try {
    const { data: fships, error: e1 } = await supabase.from('friendships')
      .select('id,requester_id,addressee_id')
      .or(`requester_id.eq.${uid},addressee_id.eq.${uid}`)
      .eq('status', 'accepted');
    if (e1) { console.error('[Amici] get friendships error:', e1); return []; }
    if (!fships?.length) return [];

    const friendIds = fships.map(f => f.requester_id === uid ? f.addressee_id : f.requester_id);
    const { data: profiles, error: e2 } = await supabase.from('profiles')
      .select('user_id,username,display_name,avatar_index,eta,peso,altezza,sesso,peso_target,livello_attivita,esperienza,obiettivo')
      .in('user_id', friendIds);
    if (e2) { console.error('[Amici] get profiles error:', e2); return []; }

    return (profiles || []).map(p => ({
      ...p,
      friendshipId: fships.find(f => f.requester_id === p.user_id || f.addressee_id === p.user_id)?.id,
    }));
  } catch(e) { console.error('[Amici] get friends exception:', e); return []; }
}

async function dbGetPending(uid) {
  try {
    const { data: fships, error: e1 } = await supabase.from('friendships')
      .select('id,requester_id')
      .eq('addressee_id', uid)
      .eq('status', 'pending');
    if (e1) { console.error('[Amici] get pending error:', e1); return []; }
    if (!fships?.length) return [];

    const requesterIds = fships.map(f => f.requester_id);
    const { data: profiles, error: e2 } = await supabase.from('profiles')
      .select('user_id,username,display_name,avatar_index,eta,peso,altezza,sesso,peso_target,livello_attivita,esperienza,obiettivo')
      .in('user_id', requesterIds);
    if (e2) { console.error('[Amici] get requester profiles error:', e2); return []; }

    return fships.map(f => ({
      id: f.id,
      requester: profiles?.find(p => p.user_id === f.requester_id) || null,
    }));
  } catch(e) { console.error('[Amici] get pending exception:', e); return []; }
}

async function dbGetPendingOut(uid) {
  try {
    const { data: fships, error: e1 } = await supabase.from('friendships')
      .select('id,addressee_id')
      .eq('requester_id', uid)
      .eq('status', 'pending');
    if (e1) { console.error('[Amici] get pending out error:', e1); return []; }
    if (!fships?.length) return [];

    const addresseeIds = fships.map(f => f.addressee_id);
    const { data: profiles, error: e2 } = await supabase.from('profiles')
      .select('user_id,username,display_name,avatar_index,eta,peso,altezza,sesso,peso_target,livello_attivita,esperienza,obiettivo')
      .in('user_id', addresseeIds);
    if (e2) { console.error('[Amici] get pending out profiles error:', e2); return []; }

    return fships.map(f => ({
      id: f.id,
      addressee: profiles?.find(p => p.user_id === f.addressee_id) || null,
    }));
  } catch(e) { console.error('[Amici] get pending out exception:', e); return []; }
}

async function dbGetFriendSessions(friendId) {
  try {
    const { data, error } = await supabase.from('sessions')
      .select('id,date,workout_name,exercises,duration')
      .eq('user_id', friendId)
      .order('date', { ascending: false })
      .limit(60);
    if (error) console.error('[Amici] get friend sessions error:', error);
    return data || [];
  } catch(e) { console.error('[Amici] get friend sessions exception:', e); return []; }
}

async function dbGetFriendSchede(friendId) {
  try {
    const { data, error } = await supabase.from('schede')
      .select('id,name,created_at')
      .eq('user_id', friendId)
      .order('id', { ascending: false })
      .limit(10);
    if (error) console.error('[Amici] get friend schede error:', error);
    return data || [];
  } catch(e) { console.error('[Amici] get friend schede exception:', e); return []; }
}

async function dbGetFriendWeightLog(friendId) {
  try {
    const { data, error } = await supabase.from('weight_log')
      .select('id,date,weight')
      .eq('user_id', friendId)
      .order('date', { ascending: false })
      .limit(30);
    if (error) console.error('[Amici] get friend weight error:', error);
    return data || [];
  } catch(e) { console.error('[Amici] get friend weight exception:', e); return []; }
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export function AmiciPage({ user, db }) {
  const uid = user?.id;
  const [friends, setFriends] = useState([]);
  const [pendingIn, setPendingIn] = useState([]);
  const [pendingOut, setPendingOut] = useState([]);
  const [friendSessions, setFriendSessions] = useState({});
  const [friendSchede, setFriendSchede] = useState({});
  const [friendWeightLog, setFriendWeightLog] = useState({});
  const [searchQ, setSearchQ] = useState('');
  const [searchRes, setSearchRes] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sent, setSent] = useState(new Set());
  const [loadingFriends, setLoadingFriends] = useState(true);
  const [acceptedName, setAcceptedName] = useState(null);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [feedTab, setFeedTab] = useState('attivita');
  const searchRef = useRef(null);

  const loadFriends = useCallback(async () => {
    if (!uid) return;
    setLoadingFriends(true);
    const [f, p, po] = await Promise.all([dbGetFriends(uid), dbGetPending(uid), dbGetPendingOut(uid)]);
    setFriends(f);
    setPendingIn(p);
    setPendingOut(po);
    setLoadingFriends(false);

    if (f.length > 0) {
      const sessMap = {}, schedeMap = {}, weightMap = {};
      await Promise.all(f.map(async fr => {
        const [s, sc, w] = await Promise.all([
          dbGetFriendSessions(fr.user_id),
          dbGetFriendSchede(fr.user_id),
          dbGetFriendWeightLog(fr.user_id),
        ]);
        sessMap[fr.user_id] = s;
        schedeMap[fr.user_id] = sc;
        weightMap[fr.user_id] = w;
      }));
      setFriendSessions(sessMap);
      setFriendSchede(schedeMap);
      setFriendWeightLog(weightMap);
    }
  }, [uid]);

  useEffect(() => { loadFriends(); }, [loadFriends]);

  const onSearchChange = q => {
    setSearchQ(q);
    clearTimeout(searchRef.current);
    if (!q.trim()) { setSearchRes([]); return; }
    setSearching(true);
    searchRef.current = setTimeout(async () => {
      const res = await dbSearchUsers(q, uid);
      setSearchRes(res);
      setSearching(false);
    }, 400);
  };

  const sendRequest = async targetId => {
    const ok = await dbSendRequest(uid, targetId);
    if (ok) setSent(s => new Set([...s, targetId]));
  };

  const respondRequest = async (fid, status, name) => {
    await dbRespond(fid, status);
    if (status === 'accepted' && name) setAcceptedName(name);
    loadFriends();
  };

  const openFriendProfile = useCallback(friendId => {
    const f = friends.find(fr => fr.user_id === friendId);
    if (f) setSelectedFriend(f);
  }, [friends]);

  const hasFriends = friends.length > 0;
  const hasSearch = searchQ.trim().length > 0;
  const hasPeopleList = friends.length > 0 || pendingIn.length > 0 || pendingOut.length > 0;

  const feedGroups = hasFriends
    ? buildUnifiedFeed(friendSessions, friendSchede, friendWeightLog, friends)
    : [];

  return (
    <div style={St.wrap}>
      {/* Header */}
      <div style={{ paddingTop:'max(56px, calc(env(safe-area-inset-top, 0px) + 14px))', paddingRight:20, paddingBottom:8, paddingLeft:20, background:C.bg }}>
        <h1 style={{ margin:0, fontFamily:FONT_BODY, fontWeight:700, fontSize:34, color:C.text, letterSpacing:-0.8, lineHeight:1.05 }}>Amici</h1>
        <p style={{ margin:'4px 0 0', fontSize:15, color:C.subtle, fontWeight:400 }}>
          {hasFriends ? `${friends.length} ${friends.length === 1 ? 'amico' : 'amici'}` : 'Connettiti con i tuoi amici'}
        </p>
      </div>

      <div style={{ padding:'0 16px 80px' }}>

        {/* Search bar */}
        <SearchBar
          value={searchQ}
          onChange={onSearchChange}
          placeholder="Cerca per @username o email…"
          style={{ marginBottom: 14 }}
        />

        {/* Search results */}
        {hasSearch && (
          <div style={{ ...St.card, marginBottom:14 }}>
            <div style={St.secTit}>Risultati</div>
            {searching && (
              <div style={{ textAlign:'center', color:C.subtle, fontSize:13, padding:'14px 0' }}>Ricerca in corso…</div>
            )}
            {!searching && searchRes.length === 0 && (
              <div style={{ textAlign:'center', color:C.muted, fontSize:13, padding:'14px 0' }}>Nessun utente trovato.</div>
            )}
            {searchRes.map((u, i, arr) => {
              const isFriend = friends.some(f => f.user_id === u.user_id);
              const isSent = sent.has(u.user_id);
              return (
                <div key={u.user_id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0',
                  borderBottom:i<arr.length-1?'0.5px solid rgba(60,60,67,0.12)':'none' }}>
                  <img src={memoUrl(u.avatar_index || 1)} alt="" style={{ width:44, height:44, borderRadius:22, background:C.fill, flexShrink:0 }}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:15, fontWeight:600, color:C.text, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                      {u.display_name || u.username || 'Utente'}
                    </div>
                    {u.username && <div style={{ fontSize:13, color:C.subtle }}>@{u.username}</div>}
                  </div>
                  {isFriend ? (
                    <span style={{ fontSize:12, fontWeight:600, color:C.green, flexShrink:0 }}>Amico</span>
                  ) : isSent ? (
                    <span style={{ fontSize:12, fontWeight:500, color:C.subtle, flexShrink:0 }}>Inviato</span>
                  ) : (
                    <button onClick={() => sendRequest(u.user_id)}
                      style={{ ...St.btn(C.blue), padding:'8px 16px', borderRadius:20, fontSize:13, flexShrink:0 }}>
                      + Aggiungi
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Success banner */}
        {acceptedName && (
          <div style={{ ...CARD_STYLE, background:'linear-gradient(135deg,#34C759 0%,#30D158 100%)',
            boxShadow:'0 6px 28px #34C75940', padding:'16px 18px', marginBottom:14,
            display:'flex', alignItems:'flex-start', gap:12 }}>
            <div style={{ width:38, height:38, borderRadius:19, background:'rgba(255,255,255,0.22)',
              display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:700, color:'#fff', marginBottom:3, fontFamily:FONT_BODY }}>
                Ora sei amico di {acceptedName}
              </div>
              <div style={{ fontSize:13, color:'rgba(255,255,255,0.85)', lineHeight:1.55, fontFamily:FONT_BODY }}>
                Adesso potete vedere i progressi l'uno dell'altro.
              </div>
            </div>
            <button onClick={() => setAcceptedName(null)}
              style={{ background:'rgba(255,255,255,0.18)', border:'none', borderRadius:999,
                width:28, height:28, cursor:'pointer', display:'flex', alignItems:'center',
                justifyContent:'center', color:'#fff', flexShrink:0 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
        )}

        {/* Segmented tab switch — shown only when there are friends or pending */}
        {(hasFriends || hasPeopleList) && !hasSearch && (
          <div style={{ background: 'rgba(120,120,128,0.12)', borderRadius: 10, padding: 3, display: 'flex', gap: 2, marginBottom: 14 }}>
            <button onClick={() => setFeedTab('attivita')} style={feedTab === 'attivita'
              ? { background: '#fff', borderRadius: 8, flex: 1, padding: '7px 0', fontSize: 13, fontWeight: 600, color: C.text, border: 'none', cursor: 'pointer', fontFamily: FONT_BODY, boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }
              : { background: 'transparent', borderRadius: 8, flex: 1, padding: '7px 0', fontSize: 13, fontWeight: 500, color: C.subtle, border: 'none', cursor: 'pointer', fontFamily: FONT_BODY }}>
              Attività
            </button>
            <button onClick={() => setFeedTab('amici')} style={feedTab === 'amici'
              ? { background: '#fff', borderRadius: 8, flex: 1, padding: '7px 0', fontSize: 13, fontWeight: 600, color: C.text, border: 'none', cursor: 'pointer', fontFamily: FONT_BODY, boxShadow: '0 1px 4px rgba(0,0,0,0.10)' }
              : { background: 'transparent', borderRadius: 8, flex: 1, padding: '7px 0', fontSize: 13, fontWeight: 500, color: C.subtle, border: 'none', cursor: 'pointer', fontFamily: FONT_BODY }}>
              I tuoi amici
            </button>
          </div>
        )}

        {/* Tab content */}
        {!hasSearch && feedTab === 'amici' && hasPeopleList && (
          <FriendsList
            friends={friends}
            friendsSessionsMap={friendSessions}
            pendingIn={pendingIn}
            pendingOut={pendingOut}
            onSelect={f => setSelectedFriend(f)}
            onRespond={respondRequest}
          />
        )}

        {!hasSearch && feedTab === 'attivita' && hasFriends && (
          feedGroups.length > 0
            ? <UnifiedFeedSection groups={feedGroups} onAvatarClick={openFriendProfile}/>
            : (
              <div style={{ textAlign:'center', padding:'32px 20px', background:'rgba(120,120,128,0.05)', borderRadius:20 }}>
                <div style={{ fontSize:13, fontWeight:500, color:C.subtle, fontFamily:FONT_BODY, lineHeight:1.6 }}>
                  Nessuna attività recente dai tuoi amici.
                </div>
              </div>
            )
        )}

        {/* Empty state */}
        {!hasSearch && !hasPeopleList && !loadingFriends && (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:28, paddingTop:20 }}>
            <OrbitAvatars />
            <div style={{ textAlign:'center' }}>
              <div style={{ fontSize:20, fontWeight:700, color:C.text, fontFamily:FONT_BODY, letterSpacing:-0.3, marginBottom:8 }}>
                Invita i tuoi amici
              </div>
              <div style={{ fontSize:15, color:C.subtle, maxWidth:260, lineHeight:1.65, fontFamily:FONT_BODY }}>
                Cerca un amico per username o email e invia una richiesta di amicizia.
              </div>
            </div>
            {_globalSettings.username && (
              <div style={{ display:'flex', alignItems:'center', gap:10, background:'rgba(0,122,255,0.07)',
                borderRadius:14, padding:'12px 16px', maxWidth:260, width:'100%' }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:11, fontWeight:600, color:C.subtle, textTransform:'uppercase', letterSpacing:0.4, marginBottom:2, fontFamily:FONT_BODY }}>Il tuo username</div>
                  <div style={{ fontSize:15, fontWeight:600, color:C.blue, fontFamily:FONT_BODY, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>@{_globalSettings.username}</div>
                </div>
                <button
                  onClick={() => navigator.clipboard?.writeText(_globalSettings.username)}
                  style={{ background:'rgba(0,122,255,0.12)', border:'none', borderRadius:8, padding:'6px 12px',
                    fontSize:12, fontWeight:600, color:C.blue, cursor:'pointer', fontFamily:FONT_BODY, flexShrink:0 }}>
                  Copia
                </button>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Friend profile overlay */}
      {selectedFriend && (
        <FriendProfileSheet
          friend={selectedFriend}
          friendSessions={friendSessions[selectedFriend.user_id]}
          myDb={db}
          onClose={() => setSelectedFriend(null)}
        />
      )}
    </div>
  );
}
